import type { ScreenContext } from "../../shell/types";
import { api, el } from "../../ui/dom";
import { ib } from "../../ui/record";
import { firstPageParams, list, type Column } from "../../ui/list";
import type { PageInfo } from "../../../server/rules/list-page";
import { openViewer } from "../../ui/viewer";
import { pageHead } from "../empty";
import { newReferral } from "../patients/patients";

// Inbox › Scans to file (brief 1B-9, mockup inbox/scans): attachments someone moved here from the
// viewer, waiting to be filed. A row (or its eye) opens the file in the viewer, where it's filed
// to a record; once filed it leaves the list.

interface Scan {
  id: string;
  file: { id: string; name: string; size: number; type: string };
  from: string;
  subject: string;
  when: string;
  movedBy: string;
}

const size = (n: number) =>
  n >= 1024 * 1024
    ? `${(n / 1024 / 1024).toFixed(1)} MB`
    : `${Math.max(1, Math.round(n / 1024))} KB`;

type Answer = PageInfo & { scans: Scan[]; startIntake: boolean };

export async function scansScreen(host: HTMLElement, ctx: ScreenContext) {
  // Brief 1E-32: the server answers one page at a time.
  const listId = "inbox/scans";
  const ask = (params: URLSearchParams) =>
    api<Answer>("GET", `/api/inbox/scans?${params.toString()}`);
  const res = await ask(firstPageParams(listId, ctx.me.user.id));
  if (!res.ok) {
    host.append(
      pageHead(ctx),
      el("p", { class: "note", role: "alert" }, "Scans to file couldn't be loaded."),
    );
    return;
  }
  const count = el("b", {}, String(res.body.total));
  const again = () => {
    if (!host.isConnected) return;
    void rows.reload();
  };
  const view = (s: Scan) => void openViewer({ file: s.file, onDone: again });
  const columns: Column<Scan>[] = [
    {
      id: "file",
      label: "File",
      text: (s) => s.file.name,
      search: true,
      cell: (s) =>
        el(
          "span",
          {},
          el("b", {}, s.file.name),
          el("br"),
          el(
            "span",
            { class: "small muted" },
            [size(s.file.size), s.from ? `from ${s.from}` : null, s.subject || null]
              .filter(Boolean)
              .join(" · "),
          ),
        ),
    },
    {
      id: "moved",
      label: "Moved here",
      text: (s) => s.when,
      cell: (s) =>
        el("span", {}, s.when, el("br"), el("span", { class: "small muted" }, s.movedBy)),
    },
    {
      id: "open",
      label: "",
      text: () => "",
      cell: (s) =>
        el(
          "span",
          { class: "rowbtns" },
          ib("eye", `Open and check ${s.file.name}`, () => view(s)),
          // Start intake from a filed scan (brief 1C-1): a referral packet makes a patient.
          res.body.startIntake
            ? ib(
                "person-add",
                `Start intake from ${s.file.name}`,
                () =>
                  void newReferral(ctx, {
                    source: "scan",
                    scanFileId: s.file.id,
                    hint: `${s.file.name} is filed to the new patient as the referral packet.`,
                  }),
              )
            : null,
        ),
    },
  ];
  const rows = list<Scan>({
    id: listId,
    userId: ctx.me.user.id,
    noun: "scans",
    caption: "Scans to file",
    searchLabel: "Search files or senders",
    first: { rows: res.body.scans, page: res.body },
    source: async (params) => {
      const r = await ask(params);
      if (!r.ok) return null;
      count.textContent = String(r.body.total);
      return { rows: r.body.scans, page: r.body };
    },
    rowId: (s) => s.id,
    columns,
    onOpen: view,
    empty: "Everything is filed.",
  });
  host.append(
    pageHead(
      ctx,
      "Anything faxed, scanned or emailed as a file, waiting to be filed. Open it, say what it is and where it goes.",
    ),
    el(
      "div",
      { class: "stagecards" },
      el("div", { class: "scard" }, count, el("span", {}, "To file")),
    ),
    rows,
  );
}
